import {
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { Router } from '@angular/router';
import { forkJoin, of, switchMap } from 'rxjs';
import { LayoutService } from '../../layouts/layout.service';
import { AuthService } from '../../auth/auth.service';
import { SearchService } from '../../core/services/search.service';
import { ProgramService } from '../../core/services/program.service';
import { CategoryService } from '../../core/services/category.service';
import { ColumnDef, DataTableComponent } from '../../shared/components/data-table/data-table';
import {
  DateRange,
  DateRangePickerComponent,
} from '../../shared/components/date-range-picker/date-range-picker';
import {
  MultiselectDropdownComponent,
  MultiselectOption,
} from '../../shared/components/multiselect-dropdown/multiselect-dropdown';
import { PersonSelectComponent } from '../../shared/components/person-select/person-select';
import { SelectComponent } from '../../shared/components/select/select';
import { STAGE_CONFIG } from '../../shared/models/incident.models';
import {
  IncidentSearchResult,
  InvestigationSearchResult,
  SearchRecordType,
  SearchResultRow,
  UnifiedSearchParams,
} from '../../shared/models/search.models';
import { apiErrorMessage } from '../../shared/utils/api-error';

type SearchMode = 'custom' | 'predefined';
type DatePreset = 'today' | 'yesterday' | 'thisWeek' | 'lastWeek' | 'thisMonth' | 'lastMonth';

const DATE_PRESETS: { id: DatePreset; label: string }[] = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'thisWeek', label: 'This week' },
  { id: 'lastWeek', label: 'Last week' },
  { id: 'thisMonth', label: 'This month' },
  { id: 'lastMonth', label: 'Last month' },
];

// Program type codes: IR = programs that record incidents, II = investigating programs.
const PROGRAM_TYPE_BY_RECORD: Record<SearchRecordType, string> = {
  incident: 'IR',
  investigation: 'II',
};

const PAGE_SIZE = 10;

@Component({
  selector: 'app-search',
  imports: [
    NgClass,
    NgTemplateOutlet,
    DataTableComponent,
    DateRangePickerComponent,
    MultiselectDropdownComponent,
    PersonSelectComponent,
    SelectComponent,
  ],
  templateUrl: './search.html',
})
export class SearchComponent implements OnInit {
  private layout = inject(LayoutService);
  private auth = inject(AuthService);
  private router = inject(Router);
  private searchService = inject(SearchService);
  private programService = inject(ProgramService);
  private categoryService = inject(CategoryService);
  private destroyRef = inject(DestroyRef);

  private categoriesTpl =
    viewChild.required<TemplateRef<{ $implicit: SearchResultRow }>>('categoriesTpl');
  private peopleTpl = viewChild.required<TemplateRef<{ $implicit: SearchResultRow }>>('peopleTpl');
  private statusTpl = viewChild.required<TemplateRef<{ $implicit: SearchResultRow }>>('statusTpl');

  protected readonly datePresets = DATE_PRESETS;

  // Record types the user may search (the API checks module access too).
  protected recordTypes = computed(() => {
    const types: { id: SearchRecordType; label: string }[] = [];
    if (this.auth.hasModuleAccess('Incidents')) types.push({ id: 'incident', label: 'Incident' });
    if (this.auth.hasModuleAccess('Investigations'))
      types.push({ id: 'investigation', label: 'Investigation' });
    return types;
  });

  // ── Criteria ──────────────────────────────────────────────────────────────
  protected mode = signal<SearchMode>('custom');
  protected recordType = signal<SearchRecordType | null>(null);
  protected dateFrom = signal('');
  protected dateTo = signal('');
  protected activePreset = signal<DatePreset | null>(null);
  protected programIds = signal<string[]>([]);
  protected categoryIds = signal<string[]>([]);
  protected personId = signal('');
  protected predefinedListId = signal('');

  protected submitAttempted = signal(false);
  protected missing = computed(() => ({
    recordType: !this.recordType(),
    dates: !this.dateFrom() || !this.dateTo(),
    predefinedList: !this.predefinedListId(),
  }));

  // ── Options ───────────────────────────────────────────────────────────────
  private programsByType = signal<Record<SearchRecordType, MultiselectOption[]>>({
    incident: [],
    investigation: [],
  });
  protected programOptions = computed(() => {
    const type = this.recordType();
    return type ? this.programsByType()[type] : [];
  });
  protected categoryOptions = signal<MultiselectOption[]>([]);
  // Predefined lists of the record types the user may search.
  private predefinedLists = signal<{ id: string; label: string; recordType: SearchRecordType }[]>(
    [],
  );
  protected predefinedListsLoaded = signal(false);
  protected predefinedListOptions = computed(() =>
    this.predefinedLists()
      .filter((l) => this.recordTypes().some((t) => t.id === l.recordType))
      .map((l) => ({
        value: l.id,
        label: `${l.label} (${l.recordType === 'incident' ? 'Incidents' : 'Investigations'})`,
      })),
  );

  // ── Results ───────────────────────────────────────────────────────────────
  protected loading = signal(false);
  protected hasSearched = signal(false);
  protected rows = signal<SearchResultRow[]>([]);
  protected total = signal(0);
  protected page = signal(1);
  protected readonly pageSize = PAGE_SIZE;
  protected errorMessage = signal<string | null>(null);
  /** Criteria of the last run, reused when paging so edits don't apply until "Run search". */
  private lastParams: UnifiedSearchParams | null = null;

  protected columns = computed((): ColumnDef<SearchResultRow>[] => [
    {
      key: 'date',
      header: 'Date',
      cell: (r) => formatDate(r.date),
      cellClass: 'text-navy-900 whitespace-nowrap',
    },
    {
      key: 'number',
      header: 'Record',
      cell: (r) => r.number,
      cellClass: 'font-bold text-navy-900 whitespace-nowrap',
    },
    {
      key: 'program',
      header: 'Program',
      cell: (r) => r.programName,
      cellClass: 'font-semibold text-navy-800 whitespace-nowrap',
    },
    { key: 'categories', header: 'Categories', cellTemplate: this.categoriesTpl() },
    { key: 'people', header: 'People', cellTemplate: this.peopleTpl() },
    { key: 'status', header: 'Status', cellTemplate: this.statusTpl() },
  ]);

  ngOnInit(): void {
    this.layout.set({
      title: 'Search',
      breadcrumb: 'Find incidents and investigations by criteria or predefined list.',
      showBackButton: false,
      switchPlaces: false,
      darkHeader: false,
    });

    // Start on the first record type the user can search (Incident when both are allowed).
    this.recordType.set(this.recordTypes()[0]?.id ?? null);

    this.loadOptions();
  }

  /** Programs for each record type (IR / II) and situation categories, loaded once. */
  private loadOptions(): void {
    this.programService
      .getProgramTypes()
      .pipe(
        switchMap((res) => {
          const idFor = (code: string) => res.programTypes.find((t) => t.code === code)?.id;
          const load = (type: SearchRecordType) => {
            const typeId = idFor(PROGRAM_TYPE_BY_RECORD[type]);
            // No pageSize → the programs endpoint returns them all.
            return typeId ? this.programService.getPrograms(undefined, typeId) : of(null);
          };
          return forkJoin({ incident: load('incident'), investigation: load('investigation') });
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (res) => {
          const toOptions = (r: typeof res.incident) =>
            (r?.programs ?? []).map((p) => ({ id: p.id, label: p.name }));
          this.programsByType.set({
            incident: toOptions(res.incident),
            investigation: toOptions(res.investigation),
          });
        },
      });

    this.searchService
      .getPredefinedLists()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (lists) => {
          this.predefinedLists.set(
            (lists ?? []).map((l) => ({
              id: l.id,
              label: l.name ?? 'Untitled list',
              recordType: l.recordType === 1 ? 'investigation' : 'incident',
            })),
          );
          this.predefinedListsLoaded.set(true);
        },
        error: () => this.predefinedListsLoaded.set(true),
      });

    this.categoryService
      .getSituationCategories()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) =>
          this.categoryOptions.set(
            res.situationCategories.map((c) => ({ id: c.id, label: c.name })),
          ),
      });
  }

  // ── Criteria handlers ─────────────────────────────────────────────────────
  protected setMode(mode: SearchMode): void {
    this.mode.set(mode);
    this.submitAttempted.set(false);
  }

  protected setRecordType(type: SearchRecordType | null): void {
    if (type === this.recordType()) return;
    this.recordType.set(type);
    // Programs differ per record type (incident programs vs investigating programs).
    this.programIds.set([]);
  }

  protected applyPreset(preset: DatePreset): void {
    const { from, to } = presetRange(preset, new Date());
    this.dateFrom.set(from);
    this.dateTo.set(to);
    this.activePreset.set(preset);
  }

  protected onRangeChange(range: DateRange): void {
    this.dateFrom.set(range.from);
    this.dateTo.set(range.to);
    this.activePreset.set(null);
  }

  protected clearCriteria(): void {
    this.setRecordType(this.recordTypes()[0]?.id ?? null);
    this.dateFrom.set('');
    this.dateTo.set('');
    this.activePreset.set(null);
    this.programIds.set([]);
    this.categoryIds.set([]);
    this.personId.set('');
    this.predefinedListId.set('');
    this.submitAttempted.set(false);
  }

  // ── Run ───────────────────────────────────────────────────────────────────
  protected runSearch(): void {
    this.submitAttempted.set(true);

    if (this.mode() === 'predefined') {
      const list = this.predefinedLists().find((l) => l.id === this.predefinedListId());
      if (!list) return;
      // The list carries its own criteria; the API needs no dates with it.
      this.lastParams = {
        recordType: list.recordType,
        predefinedListId: list.id,
        pageSize: PAGE_SIZE,
      };
      this.page.set(1);
      this.fetch();
      return;
    }

    const type = this.recordType();
    if (!type || this.missing().dates) return;

    this.lastParams = {
      recordType: type,
      dateFrom: this.dateFrom(),
      dateTo: this.dateTo(),
      programIds: this.programIds(),
      categoryIds: this.categoryIds(),
      personId: this.personId() || undefined,
      pageSize: PAGE_SIZE,
    };
    this.page.set(1);
    this.fetch();
  }

  protected onPageChange(page: number): void {
    if (!this.lastParams) return;
    this.page.set(page);
    this.fetch();
  }

  private fetch(): void {
    if (!this.lastParams) return;
    const params = { ...this.lastParams, pageIndex: this.page() - 1 };

    this.loading.set(true);
    this.errorMessage.set(null);
    this.searchService
      .search(params)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.rows.set((res.results ?? []).map((r) => toRow(r, params.recordType)));
          this.total.set(res.total ?? 0);
          this.hasSearched.set(true);
          this.loading.set(false);
        },
        error: (err) => {
          this.rows.set([]);
          this.total.set(0);
          this.hasSearched.set(true);
          this.errorMessage.set(apiErrorMessage(err, 'The search could not be completed.'));
          this.loading.set(false);
        },
      });
  }

  protected openRecord(row: SearchResultRow): void {
    const base = row.recordType === 'incident' ? '/incidents' : '/investigations';
    this.router.navigate([base, row.id]);
  }
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function toRow(
  r: IncidentSearchResult | InvestigationSearchResult,
  type: SearchRecordType,
): SearchResultRow {
  if (type === 'incident') {
    const inc = r as IncidentSearchResult;
    const stage = inc.currentStage ?? '';
    const style = STAGE_CONFIG[stage] ?? { bg: '#F1F5F9', color: '#64748B' };
    return {
      id: inc.id,
      recordType: 'incident',
      number: inc.incidentNumber ?? '—',
      date: inc.createdAt ?? null,
      programName: inc.programName ?? '—',
      categories: inc.categories ?? [],
      people: inc.personsInvolved ?? [],
      status: {
        label: !stage ? '—' : stage.toLowerCase() === 'completed' ? 'Completed' : `Stage ${stage}`,
        background: style.bg,
        color: style.color,
      },
    };
  }

  const inv = r as InvestigationSearchResult;
  // A saved finding (0 or 1) closes the investigation.
  const closed = inv.finding !== null && inv.finding !== undefined;
  return {
    id: inv.id,
    recordType: 'investigation',
    number: inv.investigationNumber ?? '—',
    date: inv.createdAt,
    programName: inv.programName ?? '—',
    categories: inv.categories ?? [],
    people: inv.personsInvolved ?? [],
    status: closed
      ? { label: 'Closed', background: '#ECFDF5', color: '#10b981' }
      : { label: 'Open', background: '#EEF4FF', color: '#4f86f7' },
  };
}

function formatDate(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function ymd(d: Date): string {
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

/** Date range for a preset, in local time. Weeks start on Sunday. */
function presetRange(preset: DatePreset, now: Date): { from: string; to: string } {
  const day = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const addDays = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
  const weekStart = addDays(day, -day.getDay());

  switch (preset) {
    case 'today':
      return { from: ymd(day), to: ymd(day) };
    case 'yesterday': {
      const y = addDays(day, -1);
      return { from: ymd(y), to: ymd(y) };
    }
    case 'thisWeek':
      return { from: ymd(weekStart), to: ymd(day) };
    case 'lastWeek':
      return { from: ymd(addDays(weekStart, -7)), to: ymd(addDays(weekStart, -1)) };
    case 'thisMonth':
      return { from: ymd(new Date(day.getFullYear(), day.getMonth(), 1)), to: ymd(day) };
    case 'lastMonth':
      return {
        from: ymd(new Date(day.getFullYear(), day.getMonth() - 1, 1)),
        to: ymd(new Date(day.getFullYear(), day.getMonth(), 0)),
      };
  }
}
